import { FC, useMemo, useState } from 'react'
import { ToolCallItem, prettyJson } from './timeline'
import {
  Tool,
  ToolContent,
  ToolHeader,
  ToolInput,
  ToolOutput,
} from '@/components/ai-elements/tool'
import { cn } from '@/lib/utils'

export const LOCATION_STYLE: Record<string, string> = {
  agent: 'bg-blue-50 text-blue-600 border-blue-200',
  frontend: 'bg-purple-50 text-purple-600 border-purple-200',
}

const PREVIEW_MAX = 80

/** 结果拍平为单行并截断(JSON 先压缩掉格式化空白) */
function oneLinePreview(raw: string): string {
  let s = raw
  try {
    s = JSON.stringify(JSON.parse(raw))
  } catch {
    /* 非 JSON,直接用原始字符串 */
  }
  s = s.replace(/\s+/g, ' ').trim()
  return s.length > PREVIEW_MAX ? s.slice(0, PREVIEW_MAX) + '…' : s
}

export function formatDuration(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`
}

/** 单行工具调用卡片:默认折叠为一行(状态+名称+位置+结果预览+耗时),点击展开参数/结果。 */
export const ToolCallCard: FC<{
  item: ToolCallItem
  onExecute?: (item: ToolCallItem) => void
}> = ({ item, onExecute }) => {
  const [open, setOpen] = useState(false)
  const isPendingFrontend = item.status === 'pending' && item.location === 'frontend' && !!onExecute

  // 空参数({}/''/null)不渲染参数大盒子
  const hasArgs = (() => {
    if (item.args == null || item.args === '') return false
    if (typeof item.args === 'object' && !Array.isArray(item.args)) {
      return Object.keys(item.args).length > 0
    }
    return true
  })()

  const preview = useMemo(() => {
    if (item.status === 'running') return '执行中…'
    if (item.status === 'pending') return item.location === 'frontend' ? '等待前端执行' : ''
    if (item.error) return item.error
    if (item.result != null) return oneLinePreview(item.result)
    return ''
  }, [item.status, item.error, item.result, item.location])

  return (
    <Tool open={open} onOpenChange={setOpen}>
      <ToolHeader
        title={item.name}
        state={item.status}
        preview={preview}
        duration={item.durationMs != null ? formatDuration(item.durationMs) : undefined}
        extra={
          <span
            className={cn(
              'shrink-0 rounded-full border px-1.5 text-[10px] font-medium leading-4',
              LOCATION_STYLE[item.location],
            )}
          >
            {item.location}
          </span>
        }
        action={
          isPendingFrontend ? (
            <button
              type="button"
              onClick={() => onExecute!(item)}
              className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground transition-opacity hover:opacity-90"
            >
              执行
            </button>
          ) : undefined
        }
      />
      <ToolContent>
        {hasArgs ? (
          <ToolInput input={item.args} />
        ) : (
          <div className="text-xs text-muted-foreground">无参数</div>
        )}
        {item.error ? (
          <ToolOutput errorText={item.error} />
        ) : item.result != null ? (
          <ToolOutput output={prettyJson(item.result)} />
        ) : null}
        {/* doc 105:break-all — 无空格长 callId 会贡献 min-content 撑爆 grid 中列 */}
        <div className="text-[11px] text-muted-foreground/70 select-all break-all">{item.callId}</div>
      </ToolContent>
    </Tool>
  )
}
